import React from 'react';
import { Card, CardContent, Typography, Box, Chip, Alert } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const formatCurrency = (value) => `$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function InsightItem({ item, type }) {
  const isUp = type === 'trend'
    ? item.direction === 'increase'
    : item.direction === 'above';

  const icon = isUp ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />;
  const color = isUp ? 'error' : 'success';

  let message;
  if (type === 'trend') {
    message = (
      <>
        <strong>{item.category}</strong> {isUp ? 'jumped' : 'dropped'}{' '}
        {formatCurrency(Math.abs(item.change_amount))} ({Math.abs(item.change_percent)}%)
        in <strong>{item.month_name}</strong> vs the prior month
      </>
    );
  } else {
    message = (
      <>
        <strong>{item.category}</strong> in <strong>{item.month_name}</strong> was{' '}
        {formatCurrency(Math.abs(item.deviation_amount))} {isUp ? 'above' : 'below'} your{' '}
        {formatCurrency(item.average)} average ({Math.abs(item.deviation_percent)}% {item.direction})
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      <Chip
        icon={icon}
        label={isUp ? (type === 'trend' ? 'Up' : 'High') : (type === 'trend' ? 'Down' : 'Low')}
        color={color}
        size="small"
        variant="outlined"
        sx={{ mt: 0.25 }}
      />
      <Typography variant="body2">{message}</Typography>
    </Box>
  );
}

export default function OutlierCard({ trends = [], outliers = [], title = 'Insights' }) {
  const hasData = trends.length > 0 || outliers.length > 0;

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{title}</Typography>

        {!hasData ? (
          <Alert severity="info" variant="outlined">
            Upload more months of data to see spending trends and outliers.
          </Alert>
        ) : (
          <>
            {trends.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Significant Month-over-Month Changes
                </Typography>
                {trends.slice(0, 5).map((t, i) => (
                  <InsightItem key={`trend-${i}`} item={t} type="trend" />
                ))}
              </Box>
            )}
            {outliers.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Spending Outliers (vs. Average)
                </Typography>
                {outliers.slice(0, 5).map((o, i) => (
                  <InsightItem key={`outlier-${i}`} item={o} type="outlier" />
                ))}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
